import React, { useMemo, useState } from 'react';
import { useBillingWorkspace, useInvoiceReminder, useMarkInvoicePaid } from '../services/api';
import useMockDataToast from '../hooks/useMockDataToast';
import { useAuth } from '../state/AuthContext';
import { useToasts } from '../state/ToastContext';

const STATUS_TABS = ['all', 'Pending', 'Installments', 'Paid'] as const;

const parseAmount = (value: string) => {
  const numeric = Number(value.replace(/[^\d.-]/g, ''));
  return Number.isFinite(numeric) ? numeric : 0;
};

const Billing: React.FC = () => {
  const { data, isLoading, isFallback } = useBillingWorkspace();
  useMockDataToast(isFallback, 'Billing service unreachable. Showing cached workspace.');
  const { invoices, insuranceQueue, inventorySpend } = data;
  const [statusFilter, setStatusFilter] = useState<(typeof STATUS_TABS)[number]>('all');
  const { hasPermission } = useAuth();
  const canApproveBilling = hasPermission('billing:approve');
  const canSendReminder = hasPermission('billing:remind') || canApproveBilling;
  const markInvoicePaid = useMarkInvoicePaid();
  const invoiceReminder = useInvoiceReminder();
  const { pushToast } = useToasts();

  const { visibleInvoices, summary, riskInvoices } = useMemo(() => {
    const totals = { pending: 0, collected: 0 };
    const normalized = invoices.map((invoice) => {
      const amount = parseAmount(invoice.balance);
      const status = (invoice.status ?? 'Pending').toLowerCase();
      if (status === 'paid' || status === 'collected') {
        totals.collected += amount;
      } else {
        totals.pending += amount;
      }
      return { ...invoice, amount, normalizedStatus: status };
    });

    const risk = normalized
      .filter((invoice) => invoice.amount >= 1000 || invoice.normalizedStatus === 'installments')
      .sort((a, b) => b.amount - a.amount)
      .slice(0, 3);

    return {
      visibleInvoices:
        statusFilter === 'all'
          ? normalized
          : normalized.filter(
              (invoice) => invoice.normalizedStatus === statusFilter.toLowerCase(),
            ),
      summary: totals,
      riskInvoices: risk,
    };
  }, [invoices, statusFilter]);

  const handleMarkPaid = async (invoiceId: string) => {
    if (!canApproveBilling) return;
    try {
      await markInvoicePaid.mutateAsync({ invoiceId });
      pushToast({ tone: 'success', message: `Invoice ${invoiceId} marked paid` });
    } catch (error) {
      pushToast({
        tone: 'error',
        message: 'Unable to mark invoice paid',
        detail: error instanceof Error ? error.message : undefined,
      });
    }
  };

  const handleSendReminder = async (invoiceId: string) => {
    if (!canSendReminder) return;
    try {
      await invoiceReminder.mutateAsync({ invoiceId, channel: 'Email' });
      pushToast({ tone: 'success', message: `Reminder sent for ${invoiceId}` });
    } catch (error) {
      pushToast({
        tone: 'error',
        message: 'Unable to send reminder',
        detail: error instanceof Error ? error.message : undefined,
      });
    }
  };

  return (
    <div className="space-y-8">
      <div>
        <p className="section-title">Revenue operations</p>
        <h1 className="text-2xl font-semibold text-slate-900">Billing workspace</h1>
        <p className="text-sm text-slate-500">
          Manage invoices, insurance claims, and cashflow signals in one console.
        </p>
      </div>

      <section className="glass-panel overflow-hidden">
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-100 px-6 py-4">
          <div>
            <h2 className="text-lg font-semibold text-slate-900">Invoice queue</h2>
            <p className="text-sm text-slate-500">Front desk and finance can collaborate live.</p>
          </div>
          <div className="flex gap-2">
            <button className="rounded-xl border border-slate-200 px-4 py-2 text-xs font-semibold text-slate-600 hover:border-brand-blue hover:text-brand-blue">
              Export ledger
            </button>
            <button
              className="rounded-xl bg-brand-blue px-4 py-2 text-xs font-semibold text-white shadow-soft disabled:opacity-50"
              disabled={!canApproveBilling}
            >
              Create invoice
            </button>
          </div>
        </div>
        {!canApproveBilling && (
          <p className="px-6 py-2 text-xs font-semibold uppercase tracking-wide text-amber-600">
            View only — billing approvals require Revenue Ops credentials.
          </p>
        )}
        <div className="flex flex-wrap items-center gap-2 border-b border-slate-100 px-6 py-2 text-xs font-semibold text-slate-500">
          {STATUS_TABS.map((tab) => (
            <button
              key={tab}
              type="button"
              onClick={() => setStatusFilter(tab)}
              className={`rounded-2xl px-3 py-1 ${
                statusFilter === tab
                  ? 'bg-brand-blue text-white'
                  : 'bg-white text-slate-500 shadow-sm'
              }`}
            >
              {tab === 'all' ? 'All statuses' : tab}
            </button>
          ))}
          <div className="ml-auto flex gap-4 text-[11px]">
            <span>
              Pending:{' '}
              <strong className="text-slate-900">
                ${Math.round(summary.pending).toLocaleString()}
              </strong>
            </span>
            <span>
              Collected:{' '}
              <strong className="text-slate-900">
                ${Math.round(summary.collected).toLocaleString()}
              </strong>
            </span>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full min-w-[680px] table-auto divide-y divide-slate-100 text-sm">
            <thead className="bg-slate-50 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
              <tr>
                <th className="px-6 py-3">Invoice</th>
                <th className="px-6 py-3">Client / Patient</th>
                <th className="px-6 py-3">Balance</th>
                <th className="px-6 py-3">Status</th>
                <th className="px-6 py-3">Due</th>
                <th className="px-6 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 bg-white text-slate-700">
              {isLoading ? (
                <tr>
                  <td colSpan={6} className="px-6 py-10 text-center text-slate-400">
                    Syncing invoice ledger...
                  </td>
                </tr>
              ) : visibleInvoices.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-6 py-10 text-center text-slate-400">
                    No invoices match the selected status.
                  </td>
                </tr>
              ) : (
                visibleInvoices.map((invoice) => (
                  <tr key={invoice.id} className="hover:bg-slate-50">
                    <td className="px-6 py-4 font-semibold text-slate-900">{invoice.id}</td>
                    <td className="px-6 py-4">
                      <p className="font-medium text-slate-900">{invoice.client}</p>
                      <p className="text-xs text-slate-500">{invoice.patient}</p>
                    </td>
                    <td className="px-6 py-4">{invoice.balance}</td>
                    <td className="px-6 py-4">
                      <span
                        className={`pill ${
                          invoice.status === 'Pending'
                            ? 'bg-amber-50 text-amber-600'
                            : invoice.status === 'Paid'
                              ? 'bg-emerald-50 text-emerald-600'
                              : 'bg-indigo-50 text-brand-indigo'
                        }`}
                      >
                        {invoice.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-slate-500">{invoice.due}</td>
                    <td className="px-6 py-4 text-right">
                      <div className="flex flex-wrap justify-end gap-2">
                        <button
                          type="button"
                          onClick={() => handleSendReminder(invoice.id)}
                          disabled={!canSendReminder || invoiceReminder.isPending}
                          className="rounded-xl border border-slate-200 px-3 py-1.5 text-[11px] font-semibold text-slate-600 transition hover:border-brand-blue hover:text-brand-blue disabled:cursor-not-allowed disabled:opacity-50"
                        >
                          Notify
                        </button>
                        <button
                          type="button"
                          onClick={() => handleMarkPaid(invoice.id)}
                          disabled={!canApproveBilling || markInvoicePaid.isPending || (invoice.status ?? '').toLowerCase() === 'paid'}
                          className="rounded-xl bg-brand-blue px-3 py-1.5 text-[11px] font-semibold text-white shadow-soft transition disabled:cursor-not-allowed disabled:opacity-50"
                        >
                          Mark paid
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </section>

      <section className="grid gap-6 lg:grid-cols-3">
        <div className="glass-panel space-y-4 p-6 lg:col-span-2">
          <div className="flex items-center justify-between">
            <div>
              <p className="section-title mb-1">Insurance</p>
              <h3 className="text-lg font-semibold text-slate-900">Claim tracker</h3>
            </div>
            <button className="rounded-xl border border-slate-200 px-4 py-2 text-xs font-semibold text-slate-600 hover:border-brand-blue hover:text-brand-blue">
              Sync carriers
            </button>
          </div>
          {isLoading ? (
            <p className="rounded-2xl border border-dashed border-slate-200 p-4 text-sm text-slate-400">
              Loading claims summary...
            </p>
          ) : (
            <ul className="space-y-3">
              {insuranceQueue.map((claim) => (
                <li
                  key={claim.claim}
                  className="flex items-center justify-between rounded-2xl border border-slate-100 bg-white px-4 py-3"
                >
                  <div>
                    <p className="text-sm font-semibold text-slate-900">
                      {claim.carrier} 繚 {claim.claim}
                    </p>
                    <p className="text-xs text-slate-500">Stage: {claim.stage}</p>
                  </div>
                  <p className="text-sm font-semibold text-brand-blue">{claim.amount}</p>
                </li>
              ))}
            </ul>
          )}
        </div>
        <div className="space-y-4">
          <div className="glass-panel space-y-4 p-6">
            <div>
              <p className="section-title mb-1">Spend</p>
              <h3 className="text-lg font-semibold text-slate-900">Inventory burn</h3>
            </div>
            {isLoading ? (
              <p className="rounded-2xl border border-dashed border-slate-200 p-4 text-sm text-slate-400">
                Aggregating spend signals...
              </p>
            ) : (
              <ul className="space-y-3 text-sm">
                {inventorySpend.map((entry) => (
                  <li key={entry.category} className="rounded-2xl border border-slate-100 bg-slate-50 p-4">
                    <p className="font-semibold text-slate-900">{entry.category}</p>
                    <p className="text-xs text-slate-500">{entry.trend}</p>
                    <p className="text-base font-semibold text-brand-blue">{entry.amount}</p>
                  </li>
                ))}
              </ul>
            )}
          </div>
          <div className="glass-panel space-y-3 p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="section-title mb-1">Escalations</p>
                <h3 className="text-lg font-semibold text-slate-900">High-risk invoices</h3>
              </div>
              <span className="rounded-full bg-rose-50 px-2 py-1 text-xs font-semibold text-rose-600">
                {riskInvoices.length}
              </span>
            </div>
            {isLoading ? (
              <p className="rounded-2xl border border-dashed border-slate-200 p-4 text-sm text-slate-400">
                Scanning escalations...
              </p>
            ) : riskInvoices.length === 0 ? (
              <p className="text-xs text-slate-500">No balances above threshold.</p>
            ) : (
              <ul className="space-y-2 text-sm">
                {riskInvoices.map((invoice) => (
                  <li
                    key={invoice.id}
                    className="rounded-2xl border border-rose-100 bg-rose-50/70 px-4 py-3 text-rose-900"
                  >
                    <div className="flex items-center justify-between">
                      <span className="font-semibold">{invoice.id}</span>
                      <span className="text-sm font-bold">{invoice.balance}</span>
                    </div>
                    <p className="text-xs">
                      {invoice.client} 繚 {invoice.patient}
                    </p>
                    <p className="text-[11px] text-rose-700">
                      Status: {invoice.status ?? 'Pending'} 繚 Due {invoice.due}
                    </p>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </section>
    </div>
  );
};

export default Billing;
